import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ContractorRole, County, Job, JobStatus } from '../shared/api';
import { COUNTY_LABELS, COUNTY_OPTIONS } from '../shared/api';
import JobsTable, { nextDeadline, sortJobs } from '../components/JobsTable';
import { Banner, Button, Card, Field, Input, Select, Spinner } from '../components/ui';
import { api } from '../lib/api';
import { ROLE_LABELS, STATUS_LABELS, STATUS_ORDER, toneForDeadline } from '../lib/format';
import { useAsync } from '../lib/useAsync';

export interface JobFilters {
  status: JobStatus | 'all';
  county: County | 'all';
  role: ContractorRole | 'all';
  urgency: 'all' | 'urgent' | 'soon' | 'watch';
}

// Deadline severity, worst first. 'info' is not a deadline state — nothing
// derives it from a date — so it ranks with 'none': never at risk.
const URGENCY_RANK = { urgent: 0, soon: 1, watch: 2, ok: 3, info: 4, none: 4 } as const;

/** Exported so the filter behaviour can be tested without mounting the page. */
export function filterJobs(jobs: Job[], f: JobFilters): Job[] {
  return jobs.filter((job) => {
    if (f.status !== 'all' && job.status !== f.status) return false;
    if (f.county !== 'all' && job.county !== f.county) return false;
    if (f.role !== 'all' && job.contractorRole !== f.role) return false;
    if (f.urgency !== 'all') {
      const tone = toneForDeadline(nextDeadline(job).iso);
      // "Watch" means watch-or-worse, so one filter shows everything at risk.
      if (URGENCY_RANK[tone] > URGENCY_RANK[f.urgency]) return false;
    }
    return true;
  });
}

const ALL: JobFilters = { status: 'all', county: 'all', role: 'all', urgency: 'all' };

export default function Jobs() {
  const [filters, setFilters] = useState<JobFilters>(ALL);
  // Archived jobs are kept but listed separately.
  const [archived, setArchived] = useState(false);
  const { data, loading, error } = useAsync(
    () => api.get<{ jobs: Job[] }>(archived ? '/api/jobs?archived=1' : '/api/jobs'),
    [archived],
  );
  const jobs = useMemo(() => data?.jobs ?? [], [data]);
  const shown = useMemo(() => sortJobs(filterJobs(jobs, filters), 'deadline'), [jobs, filters]);
  const [permitCounty, setPermitCounty] = useState<County>('palm_beach');
  const [permitNumber, setPermitNumber] = useState('');
  const [permitBusy, setPermitBusy] = useState(false);
  const [permitError, setPermitError] = useState<string | null>(null);
  const [permitHit, setPermitHit] = useState<{
    parcel: { id: string; folio: string; address: string };
    permitNumber: string;
    prime: { companyName: string | null } | null;
    seated: boolean;
  } | null>(null);

  const set = <K extends keyof JobFilters>(key: K, value: JobFilters[K]) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const findPermit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPermitBusy(true);
    setPermitError(null);
    setPermitHit(null);
    try {
      const hit = await api.get<{
        parcel: { id: string; folio: string; address: string };
        permitNumber: string;
        prime: { companyName: string | null } | null;
        seated: boolean;
      }>(`/api/parcels/by-permit?county=${encodeURIComponent(permitCounty)}&permit=${encodeURIComponent(permitNumber)}`);
      setPermitHit(hit);
    } catch (err) {
      setPermitError(err instanceof Error ? err.message : 'No file for that permit');
    } finally {
      setPermitBusy(false);
    }
  };

  if (loading) return <Spinner label="Loading jobs…" />;
  if (error) return <Banner tone="urgent" title="Could not load jobs">{error}</Banner>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-ink">{archived ? 'Archived jobs' : 'Jobs'}</h1>
          <div className="flex items-center gap-2 text-sm text-muted">
            <p>
              {shown.length} of {jobs.length} job{jobs.length === 1 ? '' : 's'}
            </p>
            <span aria-hidden>·</span>
            <button type="button" className="font-semibold text-tint hover:underline" onClick={() => setArchived((a) => !a)}>
              {archived ? 'Back to open jobs' : 'Show archived'}
            </button>
          </div>
        </div>
        <Link to="/jobs/new">
          <Button>New Job</Button>
        </Link>
      </div>

      <Card title="Find a GC job by permit">
        <form onSubmit={(e) => void findPermit(e)} className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Field label="County">
            <Select aria-label="Permit county" value={permitCounty} onChange={(e) => setPermitCounty(e.target.value as County)}>
              {COUNTY_OPTIONS.map((c) => (
                <option key={c} value={c}>{COUNTY_LABELS[c]}</option>
              ))}
            </Select>
          </Field>
          <Field label="Permit number" hint="Public number on the board. Find, not a seat.">
            <Input aria-label="Permit number" value={permitNumber} onChange={(e) => setPermitNumber(e.target.value)} placeholder="BLD-26-4412" />
          </Field>
          <div className="flex items-end">
            <Button type="submit" disabled={permitBusy || !permitNumber.trim()} className="w-full">
              {permitBusy ? 'Looking…' : 'Find GC job'}
            </Button>
          </div>
        </form>
        {permitError && <p className="mt-3 text-sm text-muted">{permitError}</p>}
        {permitHit && (
          <p className="mt-3 text-sm text-ink">
            {permitHit.prime?.companyName ?? 'A prime'} opened {permitHit.parcel.address} (folio {permitHit.parcel.folio})
            {' '}under permit {permitHit.permitNumber}. Finding it does not put you on the file.
            {permitHit.seated ? ' You already have a seat.' : ' Wait for Harbor’s invite.'}
          </p>
        )}
      </Card>

      <Card>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <label className="text-xs text-dim">
            Status
            <Select aria-label="Filter by status" value={filters.status} onChange={(e) => set('status', e.target.value as JobStatus | 'all')} className="mt-1">
              <option value="all">All statuses</option>
              {STATUS_ORDER.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS[s]}
                </option>
              ))}
            </Select>
          </label>
          <label className="text-xs text-dim">
            County
            <Select aria-label="Filter by county" value={filters.county} onChange={(e) => set('county', e.target.value as County | 'all')} className="mt-1">
              <option value="all">All counties</option>
              {COUNTY_OPTIONS.map((c) => (
                <option key={c} value={c}>
                  {COUNTY_LABELS[c]}
                </option>
              ))}
            </Select>
          </label>
          <label className="text-xs text-dim">
            Our role
            <Select aria-label="Filter by role" value={filters.role} onChange={(e) => set('role', e.target.value as ContractorRole | 'all')} className="mt-1">
              <option value="all">Any role</option>
              {(Object.keys(ROLE_LABELS) as ContractorRole[]).map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </Select>
          </label>
          <label className="text-xs text-dim">
            Urgency
            <Select aria-label="Filter by urgency" value={filters.urgency} onChange={(e) => set('urgency', e.target.value as JobFilters['urgency'])} className="mt-1">
              <option value="all">Any urgency</option>
              <option value="urgent">Due today or overdue</option>
              <option value="soon">Within 3 days</option>
              <option value="watch">Within 7 days</option>
            </Select>
          </label>
          <div className="flex items-end">
            <Button variant="secondary" onClick={() => setFilters(ALL)} className="w-full">
              Clear filters
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        <JobsTable jobs={shown} empty="No jobs match these filters." />
      </Card>
    </div>
  );
}
